import React, { useCallback, useMemo, useState } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import useCheckPermissions from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermission";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetPortfolioPlans from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import PortfolioPlanList, {
  usePortfolioPlanQueryParam,
} from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioPlanList";
import ModelTab, {
  TabKey,
} from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioPlanTab/ModelDrawer/ModelTab";
import Drawer, {
  largeDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import DropdownMenuOptions from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";

import DrawerHeader from "./DrawerHeader";

const BaseDrawer: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const history = useHistory();

  const { portfolioPlanId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
  }>();

  const location = useLocation();

  const locationPath = location.pathname.split("/");
  const locationTab = locationPath[locationPath.length - 1] as TabKey;

  const auth = useAdvisorRequestAuth();

  const { data: portfolioPlan, isLoading: isLoadingPortfolioPlan } =
    useGetPortfolioPlanById(portfolioPlanId, {}, auth);

  const { queryParams } = usePortfolioPlanQueryParam();
  const portfolioData = useGetPortfolioPlans(queryParams, auth);

  const options = useMemo(
    () =>
      (portfolioData.data?.data ?? [])
        .filter((plan) => plan.plan.portfolioPlanId !== portfolioPlanId)
        .map((plan) => ({
          id: plan.plan.portfolioPlanId,
          label: plan.plan.name,
        })),
    [portfolioData.data?.data, portfolioPlanId]
  );

  const findSelectedDrop = useCallback(
    ({ id }: { id: string; label: string }) => {
      history.push({
        ...history.location,
        pathname: `/Advisor/Investments/ModelMarketplaceV2/${id}/${locationTab}`,
      });
    },
    [history, locationTab]
  );

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const canSeeAccessTabResult = useCheckPermissions(
    { type: "PortfolioPlan", id: portfolioPlanId, action: "view:sharing" },
    auth
  );

  const canUpdatePortfolioPlan = useCheckPermissions(
    { type: "PortfolioPlan", id: portfolioPlanId, action: "update" },
    auth
  );

  const canSeeAccessTab: boolean = useMemo(() => {
    if (canSeeAccessTabResult.data === undefined) {
      return false;
    }

    return canSeeAccessTabResult.data.authorized;
  }, [canSeeAccessTabResult.data]);

  return (
    <PortfolioPlanList>
      <Drawer
        isDrawerOpen={true}
        container_style={largeDrawer}
        transitionDuration={0}
        onClose={() =>
          history.push({
            ...history.location,
            pathname: "/Advisor/Investments/ModelMarketplaceV2",
          })
        }
        header={
          <CustomDropdownBase
            curItem={
              portfolioPlan?.data.plan.name !== undefined
                ? { value: portfolioPlan?.data.plan.name }
                : undefined
            }
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            isLoading={false}
            isDisabled={false}
            width={470}
          >
            <DropdownMenuOptions
              curItemName={portfolioPlan?.data.plan.name}
              onSelect={findSelectedDrop}
              options={options}
            />
          </CustomDropdownBase>
        }
      >
        <DrawerHeader
          displayName={portfolioPlan?.data.plan.name ?? "Unknown"}
          provider={portfolioPlan?.data.plan.organization ?? "N/A"}
          isLoading={isLoadingPortfolioPlan}
          portfolioPlanId={portfolioPlanId}
          showMenuOptions={canUpdatePortfolioPlan.data?.authorized === true}
        />
        <ModelTab
          curTab={locationTab}
          portfolioPlanId={portfolioPlanId}
          showAccessTab={canSeeAccessTab}
        />
        {children}
      </Drawer>
    </PortfolioPlanList>
  );
};

export default BaseDrawer;
